import { SITE_URL } from "./config";

export interface FaqItem {
  q: string;
  a: string;
}
export interface Feature {
  icon: string;
  title: string;
  body: string;
}
export interface Step {
  title: string;
  body: string;
}
export interface LandingData {
  meta: {
    title: string;
    description: string;
    canonical: string;
    slug: string;
  };
  hero: { h1: string; subhead: string; eyebrow?: string; trustLine?: string };
  ctaText: string;
  /** Override the default signup URL for the primary CTAs. */
  ctaHref?: string;
  intro: { heading: string; body: string };
  features: Feature[];
  /** Optional caption rendered under the features grid (e.g. a disclaimer). */
  featuresNote?: string;
  /** Optional heading for the features grid (defaults to none). */
  featuresHeading?: string;
  steps: Step[];
  /** Optional "what you receive" product-interface showcase. */
  receive?: {
    heading: string;
    body: string;
    /** Which real product screenshot to show (defaults to the competitor view). */
    screenshot?: {
      key: "competitor" | "live" | "schedule" | "archive";
      alt: string;
      caption: string;
    };
  };
  /** Optional visible accuracy/transparency callout. */
  transparency?: { heading: string; body: string };
  builtFor: string;
  /** Optional audience cards rendered instead of the single builtFor line. */
  audiences?: string[];
  /** Optional comparison table. */
  comparison?: { heading: string; columns: string[]; rows: string[][] };
  faqs: FaqItem[];
}

export const LANDING_PAGES: Record<string, LandingData> = {
  "ai-competitor-monitoring": {
    meta: {
      title: "AI Competitor Monitoring and Intelligence Briefings | Meriana",
      description:
        "Track competitor launches, funding, leadership changes, pricing announcements, partnerships, and market activity. Meriana turns current public-web research into concise, cited AI briefings.",
      canonical: `${SITE_URL}/ai-competitor-monitoring/`,
      slug: "/ai-competitor-monitoring",
    },
    hero: {
      eyebrow: "AI Competitor Intelligence",
      h1: "Know What Your Competitors Are Doing—Without Constantly Searching",
      subhead:
        "Add the companies, products, or markets you want to follow. Meriana searches current public web sources on your schedule, identifies meaningful developments, and delivers a concise, cited briefing.",
      trustLine:
        "Live public-web research • Source citations • Reliability ratings • Scheduled delivery",
    },
    ctaText: "Start Monitoring Competitors",
    ctaHref: "/app/competitors/",
    intro: {
      heading: "Why raw alerts aren't enough",
      body: "Keyword alerts dump links with no judgment about what matters or whether a source is credible. Meriana researches current public web sources on your schedule, synthesizes what actually changed into a briefing, cites every source with a reliability rating, and flags when reports contradict each other — so you review evidence, not a pile of tabs.",
    },
    featuresHeading: "What Meriana monitors",
    features: [
      {
        icon: "🚀",
        title: "Product & feature launches",
        body: "Publicly announced products, features, services, integrations, and platform changes.",
      },
      {
        icon: "💰",
        title: "Funding & company activity",
        body: "Funding rounds, acquisitions, divestitures, restructurings, layoffs, and expansion announcements.",
      },
      {
        icon: "👤",
        title: "Leadership changes",
        body: "Executive hires, departures, board changes, and other publicly reported leadership moves.",
      },
      {
        icon: "🏷️",
        title: "Pricing & packaging announcements",
        body: "Publicly discoverable pricing, packaging, subscription, and plan changes.",
      },
      {
        icon: "🤝",
        title: "Partnerships & market expansion",
        body: "Partnerships, channel relationships, geographic expansion, new locations, and market-entry announcements.",
      },
      {
        icon: "📰",
        title: "News & reputation signals",
        body: "Notable coverage, sentiment patterns, controversies, customer concerns, and public response.",
      },
    ],
    featuresNote:
      "Meriana identifies pricing changes that are publicly announced or discoverable through live web research. It is not a guaranteed webpage-change detector.",
    steps: [
      {
        title: "Add competitors",
        body: "Enter a company name, product, brand, ticker, or market category — plus optional context like the website and known executives to keep the research on target.",
      },
      {
        title: "Set the research focus",
        body: "Choose what matters: product launches, pricing, funding, leadership, partnerships, expansion, customer sentiment, regulatory issues, or general developments.",
      },
      {
        title: "Choose a schedule",
        body: "Select a daily, weekly, or monthly research cadence per competitor. Pause or adjust anytime.",
      },
      {
        title: "Receive a cited briefing",
        body: "Meriana searches current public sources, synthesizes the findings, and delivers the result by email and inside the app.",
      },
    ],
    receive: {
      heading: "What you receive",
      body: "Each briefing lists the material developments Meriana found — with a plain-English summary, why each one matters, a category tag, an importance rating, a sentiment indicator, and the date detected. Every development carries direct source citations with reliability ratings so you can review the evidence yourself, and everything is archived and searchable inside the app.",
    },
    transparency: {
      heading: "Built for useful intelligence, not alert overload",
      body: "Meriana focuses on publicly discoverable information and synthesizes multiple sources into a briefing. It may not identify private, paywalled, unindexed, or quietly changed information. Every briefing makes the underlying sources visible so you can review the evidence directly — and when research finds nothing meaningful, your briefing says so instead of padding with filler.",
    },
    builtFor:
      "Founders and operators, marketing and sales leaders, investors and analysts, consultants, agencies, product teams, and business-development teams.",
    audiences: [
      "Founders & operators",
      "Marketing leaders",
      "Sales leaders",
      "Investors & analysts",
      "Consultants",
      "Agencies",
      "Product teams",
      "Business-development teams",
    ],
    comparison: {
      heading: "From raw alerts to useful intelligence",
      columns: ["Manual monitoring", "Basic alerts", "Meriana"],
      rows: [
        ["Repeated searches", "Sends links", "Synthesizes developments"],
        ["High time commitment", "High alert volume", "Scheduled briefings"],
        ["Inconsistent source review", "Little context", "Citations and source ratings"],
        ["Information spread across tools", "Keyword matching", "Competitor-focused research"],
        ["Difficult to revisit", "Limited history", "Searchable archive"],
      ],
    },
    faqs: [
      {
        q: "Does Meriana monitor the entire internet?",
        a: "No. Meriana searches current, publicly available web sources when generating a briefing. Private databases, closed platforms, paywalled content, and unindexed pages may not be accessible.",
      },
      {
        q: "Is competitor monitoring real time?",
        a: "Meriana checks for developments based on the schedule you configure — daily, weekly, or monthly per competitor — and you can also run research on demand from inside the app.",
      },
      {
        q: "Can Meriana detect competitor pricing changes?",
        a: "It can surface pricing changes that are publicly announced or discoverable through live web research. It is not a guaranteed webpage-difference monitor.",
      },
      {
        q: "Can I monitor multiple competitors?",
        a: "Yes. You can add multiple competitors, each with its own research focus and schedule, and receive individual briefings or a combined competitive digest.",
      },
      {
        q: "Does Meriana provide sources?",
        a: "Yes. Briefings include direct citations with links to the public sources used in the analysis, along with a reliability rating for each source.",
      },
      {
        q: "Can I customize what Meriana looks for?",
        a: "Yes. You choose categories such as products, pricing, funding, leadership, partnerships, expansion, customer sentiment, and regulation — and you can add focus keywords or topics to ignore.",
      },
    ],
  },

  "ai-market-intelligence": {
    meta: {
      title: "AI Market Intelligence — Automated Industry Briefings | Meriana",
      description:
        "AI market intelligence on your schedule. Meriana synthesizes sector and industry news into briefings with sentiment and source trust ratings. Try free.",
      canonical: `${SITE_URL}/ai-market-intelligence/`,
      slug: "/ai-market-intelligence",
    },
    hero: {
      h1: "AI Market Intelligence, Synthesized and Delivered Before You Need It",
      subhead:
        "Meriana turns the firehose of market, sector, and regulatory news into a clean intelligence briefing — with sentiment direction, source trust ratings, and flags when the reporting disagrees. The work an analyst does in a morning, done before you wake up.",
    },
    ctaText: "Build my intelligence brief",
    intro: {
      heading: "From noise to a position you can act on",
      body: 'Most "market intelligence" tools hand you a feed and call it a day. Meriana synthesizes. It reads across outlets on a topic — say "EU AI regulation" or "commercial real estate in Denver" — and produces a narrative rundown plus bullet takeaways, so you leave with a read on the market, not a reading list.',
    },
    features: [
      {
        icon: "📰",
        title: "Synthesized narrative",
        body: "What moved and why, in plain language. Meriana writes the brief so you can read it in two minutes and be current.",
      },
      {
        icon: "📈",
        title: "Sentiment markers",
        body: "See whether momentum around a market is building or fading — direction, not just headlines.",
      },
      {
        icon: "🛡️",
        title: "Source trust ratings",
        body: "Weight a wire report differently from a forum post. Every source carries a credibility score so you know how much to rely on it.",
      },
      {
        icon: "⚡",
        title: "Disagreement flags",
        body: "When credible sources contradict each other, Meriana tells you. These are the moments that deserve your judgment, not a summary.",
      },
    ],
    steps: [
      {
        title: "Define your markets",
        body: "Name the sectors, themes, or regulatory areas you need to track — in plain language, no configuration.",
      },
      {
        title: "Set cadence per theme",
        body: "Daily for fast-moving sectors. Monthly for slow structural trends. A different schedule for each theme.",
      },
      {
        title: "Export to your systems",
        body: "Receive briefings by email. Export via RSS or webhook to power your own dashboards, models, or reports.",
      },
    ],
    receive: {
      heading: "What a briefing looks like",
      body: "Every briefing is synthesized from live web research: sentiment on each development, a TrustRank rating on every source, contradiction flags when credible outlets disagree, and a narrative arc showing how the story has shifted.",
      screenshot: {
        key: "live",
        alt: "Meriana live briefing on artificial intelligence showing AI-synthesized market developments with sentiment labels, source citations, TrustRank reliability ratings, a contradiction flag, and a 7-day roundup",
        caption: "A live AI briefing in Meriana. Illustrative content.",
      },
    },
    builtFor:
      "Investors and analysts, corporate strategists, management consultants, and operators who get paid to see the market clearly before everyone else does.",
    faqs: [
      {
        q: "What topics can Meriana track?",
        a: "Anything you can name — a sector, a regulatory theme, a geographic market, a commodity, a macro trend, or a specific technology. You write the topic; Meriana covers it.",
      },
      {
        q: "Can I feed this into my own systems?",
        a: "Yes. Briefings export via RSS and webhook, so Meriana can flow into a dashboard, a database, or a downstream model without any manual copy-paste.",
      },
      {
        q: "How current is the intelligence?",
        a: "Briefings are generated with live web search at delivery time. Each one reflects the latest available reporting — not a stale index from last week.",
      },
      {
        q: "Does it tell me when sources disagree?",
        a: "Yes. Contradiction flags highlight when credible sources report conflicting things, so you know where to apply your own judgment rather than trusting a single narrative.",
      },
    ],
  },

  "personalized-news-tracker": {
    meta: {
      title: "Personalized News Tracker — Your Topics, AI-Curated | Meriana",
      description:
        "A personalized news tracker for the topics you choose. Meriana delivers AI-curated briefings on your schedule — no feed, no doomscrolling. Start free.",
      canonical: `${SITE_URL}/personalized-news-tracker/`,
      slug: "/personalized-news-tracker",
    },
    hero: {
      h1: "A Personalized News Tracker That Follows Your Topics — Not the Algorithm's",
      subhead:
        "Tell Meriana what you actually care about. It tracks those topics across the web and sends you a tight, AI-curated briefing on the schedule you set. No infinite feed, no engagement bait — just the news you would have wanted, already read for you.",
    },
    ctaText: "Track my topics",
    intro: {
      heading: "News that comes to you, filtered by you",
      body: "Generic news apps optimize for what keeps everyone scrolling. A personalized news tracker optimizes for you. Pick your topics — a company, a hobby, a city, an industry, a person — set how often you want them covered, and Meriana handles the rest. Block domains you don't trust, filter keywords you don't want, pause anything anytime.",
    },
    features: [
      {
        icon: "🎯",
        title: "Your topics, your words",
        body: 'Track anything you can describe — from "Tesla" to "Denver short-term rental regulation." No pre-set topic lists, no feed configuration.',
      },
      {
        icon: "🕐",
        title: "Your schedule",
        body: "Daily, weekly, or monthly — and a different cadence for every topic. You decide how often each one shows up in your inbox.",
      },
      {
        icon: "📧",
        title: "Your inbox, your way",
        body: "One combined digest or separate emails per topic. Delivered in your timezone, on your schedule.",
      },
      {
        icon: "🔍",
        title: "Your filters",
        body: "Block unwanted domains, mute keywords, and keep every briefing in a searchable archive. Your news history, always retrievable.",
      },
    ],
    steps: [
      {
        title: "Add your topics",
        body: "Name anything you want to follow. Meriana handles the searching, reading, and synthesizing.",
      },
      {
        title: "Choose your delivery",
        body: "Set cadence, timezone, and whether you want a combined digest or separate emails per topic.",
      },
      {
        title: "Read instead of scrolling",
        body: "A curated briefing in your inbox, not a feed that never ends. Every briefing is archived and searchable.",
      },
    ],
    receive: {
      heading: "Delivery on your terms",
      body: "Each topic gets its own cadence — daily, weekly, or monthly — with per-topic overrides, breaking-alert toggles, delivery times in your timezone, and one combined digest or separate emails.",
      screenshot: {
        key: "schedule",
        alt: "Meriana scheduled delivery view showing tracked topics with individual schedules, a default delivery frequency selector with daily times and days, timezone setting, and delivery email",
        caption: "The Scheduled Delivery view in Meriana. Illustrative content.",
      },
    },
    builtFor:
      "Anyone tired of choosing between staying informed and protecting their attention — professionals, investors, hobbyists, and the genuinely curious.",
    faqs: [
      {
        q: "How is this different from a news app?",
        a: "News apps push popular content at everyone. Meriana tracks only the topics you choose and delivers a curated briefing on your schedule — nothing you didn't ask for.",
      },
      {
        q: "What can I track?",
        a: "Any topic you can name: a company, an industry, a city, a person, a product, a policy area, or a niche interest. You define it in plain language.",
      },
      {
        q: "Can I control which sources appear?",
        a: "Yes. Block domains you don't trust, filter out keywords you don't want, and check the trust rating on every source in each briefing.",
      },
      {
        q: "Will it spam my inbox?",
        a: "Only as often as you ask. Set the cadence per topic, choose one combined email or separate ones, and pause anything whenever you like.",
      },
    ],
  },

  "company-monitoring-tool": {
    meta: {
      title:
        "Company Monitoring Tool — Watch Any Company, Automatically | Meriana",
      description:
        "Monitor any company automatically. Meriana tracks accounts, portfolio companies, and vendors — delivering AI briefings with source trust ratings. Start free.",
      canonical: `${SITE_URL}/company-monitoring-tool/`,
      slug: "/company-monitoring-tool",
    },
    hero: {
      h1: "The Company Monitoring Tool That Briefs You on Every Name That Matters",
      subhead:
        "Track the companies you sell to, invest in, depend on, or plan to acquire. Meriana watches each one across the web and delivers a synthesized briefing — funding, leadership, product, risk, and sentiment — so you're never the last to know.",
    },
    ctaText: "Monitor a company",
    intro: {
      heading: "One watchlist, every signal",
      body: "Whether it's your top accounts, your portfolio, your supply chain, or an acquisition target, Meriana treats each company as its own tracked topic. You get the material developments — not a feed you have to mine yourself — with source trust ratings and contradiction flags so you can be confident in what you're reading.",
    },
    features: [
      {
        icon: "🤝",
        title: "Accounts & prospects",
        body: "Walk into every client call current on your buyer's world. Know about their new initiative before they mention it.",
      },
      {
        icon: "💼",
        title: "Portfolio & investments",
        body: "Catch material news on holdings before it moves. Monitor earnings reactions, executive changes, and strategic pivots.",
      },
      {
        icon: "⚠️",
        title: "Vendors & partners",
        body: "Early warning on supplier instability, outages, or leadership changes — before they become your problem.",
      },
      {
        icon: "🔎",
        title: "Targets & diligence",
        body: "Build a continuous picture of a company you're evaluating. Every briefing is archived so your diligence file builds itself.",
      },
    ],
    steps: [
      {
        title: "Add each company",
        body: "Name any company by its name or ticker. Public or private — if it generates news, Meriana can track it.",
      },
      {
        title: "Set cadence and delivery",
        body: "Combined digest or one email per company. Daily for key accounts, weekly for the broader watchlist.",
      },
      {
        title: "Search your history",
        body: 'Every briefing is archived and fully searchable. "What did they announce last quarter?" is one search away.',
      },
    ],
    receive: {
      heading: "What a company briefing looks like",
      body: "Each briefing distills the company's latest developments into cited bullets with sentiment labels and TrustRank source ratings — plus contradiction flags when outlets disagree and a running narrative of how the story is evolving.",
      screenshot: {
        key: "live",
        alt: "Meriana live briefing showing a tracked company topic with AI-synthesized developments, sentiment labels, source citations, TrustRank reliability ratings, a contradiction flag, and a 7-day roundup",
        caption: "A live company briefing in Meriana. Illustrative content.",
      },
    },
    builtFor:
      "Sales and account teams, investors and analysts, procurement and risk, and anyone running diligence who needs a company's story to stay current without staying glued to it.",
    faqs: [
      {
        q: "How many companies can I monitor?",
        a: "As many as you want — each company is its own tracked topic with its own schedule and delivery settings.",
      },
      {
        q: "Can I monitor private companies, not just public ones?",
        a: "Yes. If a company generates news anywhere on the web, Meriana can track it. You're not limited to tickers or public filings.",
      },
      {
        q: "Does it keep a history I can reference later?",
        a: "Yes. Every briefing is archived and fully searchable, so your monitoring builds into a running record on each company over time.",
      },
      {
        q: "How do I know the information is reliable?",
        a: "Every source carries a trust rating, and Meriana flags when credible sources disagree, so you can weigh the reporting and know what's contested.",
      },
    ],
  },

  "ai-research-assistant": {
    meta: {
      title: "AI Research Assistant — Your 24/7 Intelligence Analyst | Meriana",
      description:
        "An AI research assistant that reads the web for you. Meriana delivers synthesized briefings with cited sources and trust ratings on your schedule. Try free.",
      canonical: `${SITE_URL}/ai-research-assistant/`,
      slug: "/ai-research-assistant",
    },
    hero: {
      h1: "An AI Research Assistant That Does the Reading Before You're Awake",
      subhead:
        "Meriana is a 24/7 research analyst that never sleeps and never pads. Give it the topics you need to stay on top of; it reads across the web, synthesizes what matters, cites its sources, and delivers a briefing on your schedule.",
    },
    ctaText: "Put my analyst to work",
    intro: {
      heading: "A research analyst, not a chatbot",
      body: "Ask a general AI tool a question and you get a one-off answer you have to re-ask tomorrow. Meriana is standing research: it tracks your topics continuously, remembers everything in a searchable archive, and proactively delivers what changed — with source citations and trust ratings so you can verify anything in seconds. The five hours of reading you don't have time for, done for you, every time.",
    },
    features: [
      {
        icon: "📄",
        title: "Synthesized briefings, not link dumps",
        body: "The narrative and the bullets, written for a busy reader. Not a pile of URLs to open later.",
      },
      {
        icon: "🛡️",
        title: "Cited sources with trust ratings",
        body: "Every claim in every briefing is checkable. Sources carry credibility scores so you know how much weight to give each one.",
      },
      {
        icon: "⚡",
        title: "Contradiction flags",
        body: "When credible sources disagree, the AI tells you — so you know where to think for yourself rather than trusting one narrative.",
      },
      {
        icon: "🗂️",
        title: "A searchable archive",
        body: "Everything Meriana has ever briefed you on is archived and searchable. Your research compounds instead of resetting each session.",
      },
    ],
    steps: [
      {
        title: "Tell it what to follow",
        body: "Name the topics, companies, and markets you need to stay on top of. Plain language, no configuration.",
      },
      {
        title: "Set how it reports",
        body: "Choose frequency per topic and how you want delivery — combined digest or separate. Daily, weekly, or monthly.",
      },
      {
        title: "Read, search, export",
        body: "Get the briefing in your inbox. Search the archive anytime. Export via RSS or webhook to your own tools.",
      },
    ],
    receive: {
      heading: "Your research, on the record",
      body: "Every briefing is saved to a searchable archive with sentiment trends per topic, so your assistant's research compounds over time instead of vanishing — reopen, re-run, or share any past briefing.",
      screenshot: {
        key: "archive",
        alt: "Meriana archive view showing searchable past briefings organized by date with per-topic sentiment trend bars for positive, negative, and mixed coverage",
        caption: "The searchable briefing archive in Meriana. Illustrative content.",
      },
    },
    builtFor:
      "Consultants, researchers, founders, analysts, and knowledge workers whose job is to know things — who'd rather spend their hours deciding than reading.",
    faqs: [
      {
        q: "How is this different from ChatGPT or Perplexity?",
        a: "Those answer one question at a time when you ask them. Meriana works continuously — it tracks your topics, delivers updates on a schedule you set, and keeps a searchable archive. Standing analyst, not chatbot.",
      },
      {
        q: "Does it cite its sources?",
        a: "Yes. Every briefing includes cited sources with trust ratings, and flags when sources contradict each other, so you can verify any claim in seconds.",
      },
      {
        q: "Can it remember what it's researched?",
        a: "Yes. Every briefing is archived and fully searchable, so your research builds over time instead of disappearing when you close the window.",
      },
      {
        q: "What does it run on?",
        a: "Meriana is powered by Google's Gemini model with live web search, so briefings reflect current information rather than a fixed training cutoff.",
      },
    ],
  },

  "track-industry-trends": {
    meta: {
      title: "Track Industry Trends Automatically — AI Briefings | Meriana",
      description:
        "Track industry trends automatically. Meriana surfaces emerging shifts and delivers synthesized AI briefings on your schedule with sentiment markers. Start free.",
      canonical: `${SITE_URL}/track-industry-trends/`,
      slug: "/track-industry-trends",
    },
    hero: {
      h1: "Track Industry Trends Automatically — and Be First to See Where It's Going",
      subhead:
        "Stop manually scanning for what's next in your field. Meriana tracks your industry across the web, surfaces the shifts that matter, and delivers a synthesized briefing on your schedule — with sentiment markers that show you which way the wind is turning before it's obvious.",
    },
    ctaText: "Track my industry",
    intro: {
      heading: "Trends, not just headlines",
      body: "Anyone can read today's news. The edge is seeing the pattern early. Meriana tracks your industry continuously and synthesizes coverage into a read on direction — what's gaining momentum, what's losing it, where the credible sources disagree — so you spot the trend while it's still a signal, not after it's a headline.",
    },
    features: [
      {
        icon: "🔁",
        title: "Set it once, track forever",
        body: "Name your industry or theme. Meriana watches it indefinitely, updating each briefing with the latest from across the web.",
      },
      {
        icon: "📈",
        title: "Sentiment over time",
        body: "See whether momentum around a trend is building or fading. Direction matters as much as the news itself.",
      },
      {
        icon: "⚡",
        title: "Contested calls, surfaced",
        body: "The trends worth spotting early are usually contested. Contradiction flags show you where the smart money disagrees — before consensus forms.",
      },
      {
        icon: "🗂️",
        title: "A searchable record",
        body: "Look back across briefings to confirm a trend is real and sustained, not a one-week blip. Your archive makes the pattern visible.",
      },
    ],
    steps: [
      {
        title: "Define your industries",
        body: "Name the sectors and themes you want to stay ahead of. As specific or broad as you need.",
      },
      {
        title: "Choose your cadence",
        body: "Weekly is ideal for trend-tracking. Daily for fast-moving fields like AI, biotech, or policy.",
      },
      {
        title: "Brief, search, export",
        body: "Read the briefing in your inbox. Export via RSS or webhook to power your own reports and decks.",
      },
    ],
    receive: {
      heading: "What a trend briefing looks like",
      body: "Sentiment markers on every development show which way coverage is leaning, TrustRank ratings weigh each source, and the narrative arc traces how the story has shifted week over week.",
      screenshot: {
        key: "live",
        alt: "Meriana live briefing on an industry topic showing trend developments with sentiment markers, source citations, TrustRank reliability ratings, a contradiction flag, and a 7-day roundup",
        caption: "A live trend briefing in Meriana. Illustrative content.",
      },
    },
    builtFor:
      "Strategists, founders, product leaders, marketers, and analysts who'd rather set the agenda than react to it.",
    faqs: [
      {
        q: "How does Meriana spot trends and not just news?",
        a: "By tracking a topic continuously and synthesizing across sources over time, Meriana surfaces direction and momentum — including sentiment shifts — rather than isolated headlines.",
      },
      {
        q: "How often does it update?",
        a: "On whatever schedule you set — daily, weekly, or monthly per topic. Weekly is the most common choice for trend-tracking; daily suits fast-moving fields.",
      },
      {
        q: "Can I use the briefings in my own reports?",
        a: "Yes. Export any briefing via RSS or webhook, and search the archive whenever you're building a report, a deck, or a strategy document.",
      },
      {
        q: "Do I need to keep configuring it?",
        a: "No. Set your industry and cadence once. Meriana tracks it automatically until you change or pause it.",
      },
    ],
  },
};

export const LANDING_SLUGS = Object.keys(LANDING_PAGES);
